import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      navigate('/dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${API}/payments/checkout/status/${sessionId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === 'paid') {
          setStatus('success');
          setPaymentDetails(data);
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Payment status error:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="grid-background absolute inset-0 opacity-20"></div>
      
      <div className="relative z-10 w-full max-w-md text-center">
        {status === 'checking' && (
          <div>
            <div className="w-24 h-24 border-4 border-[#00f0ff] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
            <h2 className="text-3xl font-bold text-[#00f0ff] glow mb-4">Processing Payment...</h2>
            <p className="text-[#00f0ff]/70">Please wait while we confirm your payment</p>
          </div>
        )}

        {status === 'success' && (
          <div className="glass rounded-2xl p-8 glow-box" data-testid="payment-success">
            <CheckCircle className="w-24 h-24 text-[#00ff00] mx-auto mb-6 glow" />
            <h2 className="text-4xl font-bold text-[#00ff00] glow mb-4">Payment Successful!</h2>
            <p className="text-[#00f0ff]/70 mb-2">Thank you for your payment</p>
            {paymentDetails && (
              <p className="text-2xl font-bold text-[#00f0ff] mb-8">
                ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency.toUpperCase()}
              </p>
            )}
            <p className="text-[#00f0ff]/60 mb-8">Our team will start working on your request shortly.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-8 py-3 btn-primary text-white font-bold rounded-lg flex items-center justify-center space-x-2 mx-auto"
              data-testid="back-to-dashboard-button"
            >
              <span>Back to Dashboard</span>
              <ArrowRight size={20} />
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className="glass rounded-2xl p-8 glow-box">
            <div className="text-6xl mb-6">❌</div>
            <h2 className="text-3xl font-bold text-[#ff0000] glow mb-4">Payment Error</h2>
            <p className="text-[#00f0ff]/70 mb-8">There was an error processing your payment. Please try again.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-8 py-3 border-2 border-[#00f0ff] text-[#00f0ff] font-bold rounded-lg hover:bg-[#00f0ff] hover:text-[#0a0a1f] transition-all"
            >
              Back to Dashboard
            </button>
          </div>
        )}

        {status === 'timeout' && (
          <div className="glass rounded-2xl p-8 glow-box">
            <div className="text-6xl mb-6">⏱️</div>
            <h2 className="text-3xl font-bold text-[#ffa500] glow mb-4">Checking Payment...</h2>
            <p className="text-[#00f0ff]/70 mb-8">Payment verification is taking longer than expected. Please check your email for confirmation.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-8 py-3 btn-primary text-white font-bold rounded-lg"
            >
              Back to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentSuccess;